import { Injectable } from '@nestjs/common';
import { createHash } from 'node:crypto';
import * as fs from 'node:fs';
import * as path from 'node:path';
import {
  ConnectionProbeResult,
  ListPage,
  ObjectMetadata,
  PresignedUpload,
  SaveResult,
  StorageAdapter,
} from './storage/storage-adapter';

/**
 * Local-disk storage, retained **only as a migration source**.
 *
 * Primary media no longer lives on the application server (see
 * docs/19-media-management-plan.md §13): every environment writes to object
 * storage, so there is no code path that runs only in production. This adapter
 * stays so that objects written before that change remain readable and
 * deletable while the copy job moves them, and it is removed once every
 * organization's local_disk config reaches an asset count of zero.
 *
 * New uploads must not be routed here. `createUploadUrl` therefore throws
 * rather than silently degrading to a proxied write — a presigned upload that
 * lands on the app server is exactly what this plan set out to stop.
 */
@Injectable()
export class LocalDiskImageStorageAdapter implements StorageAdapter {
  private readonly root = process.env.UPLOADS_DIR ?? path.join(process.cwd(), 'uploads');

  private resolve(key: string): string {
    const target = path.resolve(this.root, key);
    // Containment check: a key is caller data, and `..` in one would otherwise
    // read or delete arbitrary files on the server.
    const root = path.resolve(this.root);
    if (target !== root && !target.startsWith(root + path.sep)) {
      throw new Error(`Key escapes the uploads root: ${key}`);
    }
    return target;
  }

  async save(buffer: Buffer, key: string): Promise<SaveResult> {
    const filePath = this.resolve(key);
    await fs.promises.mkdir(path.dirname(filePath), { recursive: true });
    await fs.promises.writeFile(filePath, buffer);
    // /api/uploads, not a bare /uploads — see main.ts's useStaticAssets prefix
    // for why: every frontend's proxy/reverse-proxy only forwards /api/*.
    return {
      url: this.publicUrl(key),
      sizeBytes: buffer.length,
      storageKey: key,
      checksum: createHash('sha256').update(buffer).digest('hex'),
    };
  }

  read(key: string): Promise<Buffer> {
    return fs.promises.readFile(this.resolve(key));
  }

  async delete(key: string): Promise<void> {
    await fs.promises.rm(this.resolve(key), { force: true });
  }

  async deleteMany(keys: string[]): Promise<void> {
    for (const key of keys) await this.delete(key);
  }

  async exists(key: string): Promise<boolean> {
    try {
      await fs.promises.access(this.resolve(key));
      return true;
    } catch {
      return false;
    }
  }

  async getMetadata(key: string): Promise<ObjectMetadata> {
    const stat = await fs.promises.stat(this.resolve(key));
    return { sizeBytes: stat.size, mimeType: guessMime(key), lastModified: stat.mtime };
  }

  /** Single page, no cursor: the tree is finite and this only feeds migration. */
  async list(prefix: string): Promise<ListPage> {
    const base = this.resolve(prefix);
    const keys: string[] = [];
    const walk = async (dir: string): Promise<void> => {
      let entries: fs.Dirent[];
      try {
        entries = await fs.promises.readdir(dir, { withFileTypes: true });
      } catch {
        return;
      }
      for (const entry of entries) {
        const full = path.join(dir, entry.name);
        // The derivative cache is derived data, not merchant media — it must
        // never be migrated or counted against a quota.
        if (entry.isDirectory()) {
          if (entry.name === '.derivatives') continue;
          await walk(full);
        } else {
          keys.push(path.relative(this.root, full).split(path.sep).join('/'));
        }
      }
    };
    await walk(base);
    return { keys };
  }

  publicUrl(key: string): string {
    return `/api/uploads/${key.replace(/\\/g, '/')}`;
  }

  async createDownloadUrl(key: string): Promise<string> {
    return this.publicUrl(key);
  }

  async createUploadUrl(): Promise<PresignedUpload> {
    throw new Error('Local disk storage does not support presigned uploads — configure object storage.');
  }

  async testConnection(): Promise<ConnectionProbeResult> {
    const ok = fs.existsSync(this.root);
    return {
      ok,
      canList: ok,
      canWrite: ok,
      canRead: ok,
      canDelete: ok,
      publicReadOk: ok,
      state: ok ? 'connected' : 'config_error',
      message: ok ? 'Legacy local disk storage (migration source only)' : `Uploads root missing: ${this.root}`,
    };
  }
}

function guessMime(key: string): string {
  const ext = path.extname(key).toLowerCase();
  if (ext === '.png') return 'image/png';
  if (ext === '.webp') return 'image/webp';
  if (ext === '.gif') return 'image/gif';
  if (ext === '.svg') return 'image/svg+xml';
  if (ext === '.pdf') return 'application/pdf';
  return 'image/jpeg';
}
